import { useEffect, useState } from 'react';

const KEY = 'studio.theme';
// Pre-unification the workbench kept its own key — honour it once as a
// fallback so nobody's saved choice resets, then write forward under the
// shared key.
const LEGACY_KEY = 'workbench.theme';

export type Theme = 'dark' | 'light';

function initialTheme(): Theme {
  try {
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (raw !== null) {
      // Checked rather than trusted. JSON.parse is `any`, and localStorage is
      // shared across the studio, the workbench and the public category pages
      // on one origin — so a stale or hand-edited value used to be written
      // straight onto `data-theme`. An unrecognised value now falls through to
      // the OS preference, which is what an unset key already does.
      const parsed: unknown = JSON.parse(raw);
      if (parsed === 'dark' || parsed === 'light') return parsed;
    }
  } catch {
    /* unreadable storage — fall through to the OS preference */
  }
  const prefersLight = typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches;
  return prefersLight ? 'light' : 'dark';
}

/**
 * App-chrome theme (the slides themselves are always black — brand rule;
 * this only themes the UI around the preview). One localStorage key across
 * the studio pages and the workbench — same origin, so flipping it on one
 * surface carries to the others. Defaults to the OS preference.
 */
export function useTheme() {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(KEY, JSON.stringify(theme));
    } catch {}
  }, [theme]);

  // `as const` so callers destructure `[Theme, () => void]` rather than an
  // array of the union of both.
  return [theme, () => setTheme(theme === 'dark' ? 'light' : 'dark')] as const;
}
